/**
 * ExternalAnimationCanvas - Animation canvas that uses external state instead of context
 * Use this when you need multiple independent animations (e.g., multi-play slides)
 */

import { useRef } from 'react'
import { AnimationCanvasRenderer } from './AnimationCanvasRenderer'
import { useDimensions, useFieldCoordinates } from '@/hooks'
import { usePlayerInfoMap } from '@/hooks/UsePlayerInfoMap'
import { useRouteProgressMap } from '@/hooks/UseRouteProgressMap'
import { shouldShowAnimation } from '@/utils/Animation.utils'
import type { Drawing, Player } from '@/types'
import type { AnimationState } from '@/types/Animation.types'

type ExternalAnimationCanvasProps = {
	/** Drawings to render */
	drawings: Drawing[]
	/** Players to render */
	players: Player[]
	/** External animation state from useAnimationInstance */
	animationState: AnimationState
	/** Optional class name */
	className?: string
}

/**
 * Renders an animated play using external animation state.
 * Unlike AnimationCanvas, this component doesn't use AnimationContext.
 */
export function ExternalAnimationCanvas({
											drawings,
											players,
											animationState,
											className = '',
										}: ExternalAnimationCanvasProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const dimensions = useDimensions(containerRef)

	const coordSystem = useFieldCoordinates({
		containerWidth: dimensions.width,
		containerHeight: dimensions.height,
	})

	const playerInfo = usePlayerInfoMap(players)
	const routeProgress = useRouteProgressMap(animationState.playerStates)

	// Show animation if playing or has been started
	const showAnimation = shouldShowAnimation(animationState)

	if (dimensions.width === 0 || dimensions.height === 0) {
		return (
			<div
				ref={containerRef}
				className={`size-full ${className}`.trim()}
			/>
		)
	}

	return (
		<div
			ref={containerRef}
			className={`relative size-full overflow-hidden ${className}`.trim()}
		>
			<AnimationCanvasRenderer
				drawings={drawings}
				animationState={animationState}
				coordSystem={coordSystem}
				playerInfo={playerInfo}
				routeProgress={routeProgress}
				showAnimation={showAnimation}
			/>
		</div>
	)
}
